import React, {createContext, useContext, useState, useEffect, useCallback, useMemo, useRef} from 'react';
import {useLocation} from "react-router-dom";
import {AccessService} from "../utils/AccessService";
import {APIClient} from "../utils/APIClient";
import {API_URL} from "../settings/local_settings";
import {LocalStorage} from "../utils/LocalStorage";

/**
 * Fuente única de los flags del usuario (`GetUserModules`): módulos, tier de experiencia de la
 * agencia y las capabilities que derivan de él. Este provider existe para que el marco de la app
 * (sidebar + topbar) no sume otro fetch del mismo endpoint y para que las pantallas se cuelguen de
 * acá en vez de pedirlo por su cuenta.
 *
 * EL TIER (`experience_tier`, backend: core/services/agency_experience.py) es el SSOT del rollout:
 *   BASE  → la app de siempre.
 *   IA    → BASE + Memoria del Cliente + Media Plan. Sin rediseño.
 *   UX_21 → IA + ficha 2.1 + marco nuevo + el switch para volver al clásico.
 *
 * Además resuelve QUÉ VERSIÓN DE UI ve el usuario. Reglas, en orden:
 *   1. Usuario-cliente (portal) o sin agencia → siempre v1 (el portal tiene su propio layout).
 *   2. Agencia fuera de UX_21 → siempre v1. Fail-closed: si el fetch falla y no hay cache, v1.
 *   3. `?ui=v1|v2` en la URL → override explícito (para linkear y QA); se persiste.
 *   4. Preferencia guardada por el usuario en este browser (el switch del menú de usuario).
 *   5. Default para el piloto → v2.
 *
 * De ahí sale `ficha21` — el flag que gobierna la ficha del cliente rediseñada — que NO es sólo el
 * tier: es `tier UX_21 && el usuario no volvió al clásico`. Antes la ficha (`ficha_v2`) y el marco
 * (`ui_v2`) eran dos flags de agencia independientes, y el resultado era un híbrido: "Volver al
 * Dashbo clásico" cambiaba el sidebar pero dejaba el Panel, el tab IA unificado y la Memoria
 * rediseñada. Hoy el switch mueve al usuario a la experiencia COMPLETA de un tier IA: marco viejo,
 * ficha vieja, y Memoria y Media Plan intactos (esos son del tier, no del rediseño).
 *
 * Y, sólo en v2, centraliza los datos que consume el marco (nombre del usuario, foto de perfil, flag
 * de superadmin, agencia). Van acá y no en el chrome porque `AppChromeV2` se monta DENTRO de cada
 * pantalla y remonta en cada navegación: pedirlos ahí significaba 3 requests por click. En v1 no se
 * piden — ese camino lo sigue resolviendo `Layout.js` como siempre.
 *
 * Los tres fetches (módulos, identidad, agencia) arrancan de un cache en localStorage y van con
 * `{cache: true}` para compartir el request con los otros callers del mismo endpoint en el arranque.
 * Lo primero es lo que hace que el marco no parpadee; lo segundo, que la home no pida `access/modules`
 * dos veces ni `get-logged-user-data/` tres.
 */

export const UI_VERSION_V1 = 'v1';
export const UI_VERSION_V2 = 'v2';

export const TIER_BASE = 'BASE';
export const TIER_IA = 'IA';
export const TIER_UX_21 = 'UX_21';

// Preferencia explícita del usuario (el switch viejo↔nuevo). Sobrevive al logout a propósito: es del
// usuario en este browser, no de la sesión. Va keyed por usuario (card OGGNnRpB): global, un navegador
// que eligió el clásico con una cuenta mandaba al clásico a la siguiente — incluida una agencia recién
// creada, que nace en la 2.1 y así no llegaba al túnel.
const UI_VERSION_PREF_PREFIX = 'dashbo_ui_version_u';
// La clave global de antes. La ADOPTA una sola vez el primer usuario que la lee y se borra: no lleva
// datos de la agencia (sólo «v1» o «v2») y descartarla devolvería a la 2.1, sin aviso, a quien había
// elegido el clásico. Casi siempre quien la lee primero después del deploy es quien la eligió.
const LEGACY_UI_VERSION_PREF_KEY = 'dashbo_ui_version';
// Cache del payload de módulos. Cumple dos funciones: evitar que el marco parpadee de v1 a v2 en el
// primer render, y sostener la navegación si el endpoint no responde. Va keyed por usuario para que
// la sesión de otro no herede flags ajenos (aislamiento cross-agency).
const MODULES_CACHE_KEY = 'dashbo_user_modules_cache';
const AGENCY_CACHE_KEY = 'dashbo_agency_data_cache';
// Identidad del usuario para el marco (nombre + foto + superadmin). Existe por la misma razón que
// el cache de módulos: sin él, el avatar arranca SIEMPRE en las iniciales y recién muta a la foto
// cuando termina la cadena `access/modules` → `get-logged-user-data/` → descarga de la imagen —
// medido en prod, ~0,7s con caché caliente y ~1,9s en frío. El usuario lo lee como "se rompió la
// foto de perfil", y en cada F5. Va keyed por usuario, igual que los otros dos.
const CHROME_CACHE_KEY = 'dashbo_user_chrome_cache';
// Edad a partir de la cual el payload de módulos se vuelve a pedir al navegar o al volver a la
// pestaña. Es el TTL del cache de suscripción del backend: antes, el backend devolvería lo mismo.
export const MODULES_STALE_MS = 60 * 1000;

function readJson(key) {
    try {
        return JSON.parse(localStorage.getItem(key) || 'null');
    } catch {
        return null;
    }
}

function writeJson(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch { /* modo incógnito / storage lleno: se pierde el render optimista, nada más */ }
}

function readCachedFor(key, userId) {
    const cached = readJson(key);
    return (cached && String(cached.userId) === String(userId)) ? cached.value : null;
}

function isUiVersion(value) {
    return value === UI_VERSION_V1 || value === UI_VERSION_V2;
}

function uiVersionPrefKey(userId) {
    return `${UI_VERSION_PREF_PREFIX}${userId}`;
}

function readUiVersionPreference(userId) {
    if (!userId) return null;
    try {
        const key = uiVersionPrefKey(userId);
        let stored = localStorage.getItem(key);
        if (stored === null) {
            const legacy = localStorage.getItem(LEGACY_UI_VERSION_PREF_KEY);
            if (legacy !== null) {
                localStorage.removeItem(LEGACY_UI_VERSION_PREF_KEY);
                if (isUiVersion(legacy)) localStorage.setItem(key, legacy);
                stored = legacy;
            }
        }
        return isUiVersion(stored) ? stored : null;
    } catch {
        return null;
    }
}

function writeUiVersionPreference(userId, version) {
    if (!userId) return;
    try {
        localStorage.setItem(uiVersionPrefKey(userId), version);
    } catch { /* ver arriba */ }
}

function readUiVersionFromUrl() {
    try {
        const value = new URLSearchParams(window.location.search).get('ui');
        return (value === UI_VERSION_V1 || value === UI_VERSION_V2) ? value : null;
    } catch {
        return null;
    }
}

function readSession() {
    return {
        userId: LocalStorage.fetchUserId() || null,
        isClientUser: LocalStorage.isClientUser(),
    };
}

const EMPTY_STATE = {
    modules: null,
    loaded: false,
    experienceTier: TIER_BASE,
    mediaPlanEnabled: false,
    clientMemoryLevel: AccessService.MEMORY_LEVEL_OFF,
    feedbackWidgetEnabled: false,
    guidesEnabled: false,
    clientEconomicsEnabled: false,
    alertRulesEnabled: false,
    systemAlertsConverted: false,
    uncontrolledSpendEnabled: false,
    shopifyInstallByLink: false,
};

/**
 * El tier del payload. El fallback NO es cosmético: `MODULES_CACHE_KEY` guarda en localStorage el
 * último payload visto, y durante la ventana entre el deploy del backend y el de este repo (o
 * simplemente hasta el primer fetch exitoso) ese cache tiene el shape viejo, sin `experience_tier`.
 * Sin derivarlo de los flags viejos, el primer render dejaría a una agencia del piloto en BASE.
 */
export function tierFromPayload(payload) {
    if (payload.experience_tier) return payload.experience_tier;
    if (payload.ui_v2 === true || payload.ficha_v2 === true) return TIER_UX_21;
    if (payload.client_memory_level && payload.client_memory_level !== AccessService.MEMORY_LEVEL_OFF) return TIER_IA;
    return TIER_BASE;
}

export function stateFromPayload(payload, loaded = true) {
    const experienceTier = tierFromPayload(payload);
    return {
        modules: payload.modules || null,
        loaded,
        experienceTier,
        // El backend viejo no manda `media_plan_enabled`; el tier IA lo implica.
        mediaPlanEnabled: payload.media_plan_enabled === true || (
            payload.media_plan_enabled === undefined && experienceTier !== TIER_BASE),
        clientMemoryLevel: payload.client_memory_level || AccessService.MEMORY_LEVEL_OFF,
        // Widget de feedback: la mitad de AGENCIA de la regla (la agencia tiene la UX 2.1), ya
        // resuelta por el backend. A diferencia de `mediaPlanEnabled`, acá NO se deriva nada:
        // comparación estricta contra `true`, fail-closed. La otra mitad —que el usuario esté
        // viendo la 2.1 y no el clásico— la pone `useFeedbackWidget`.
        feedbackWidgetEnabled: payload.feedback_widget_enabled === true,
        // Guías in-app: mismo criterio fail-closed que el widget. Sin el flag, el FE ni pide las guías.
        guidesEnabled: payload.guides_enabled === true,
        // Economía del cliente (módulo de suscripción, apagado por defecto). Viaja como bool propio
        // y no en `modules`, igual que `media_plan_enabled`. Fail-closed como los dos de arriba: el
        // cache de localStorage del shape viejo no trae el campo, y eso es «no», nunca «sí».
        clientEconomicsEnabled: payload.client_economics_enabled === true,
        // Centro de Alertas (tanda 2026-10-06, card JdTwLEQK): flag de ROLLOUT de la agencia
        // (`Agency._alert_rules_enabled`), resuelto por el gate del backend que además exige la 2.1.
        // Fail-closed como los de arriba: sin el campo (cache viejo, backend sin deployar) no hay
        // menú, ni rutas, ni links nuevos.
        alertRulesEnabled: payload.alert_rules_enabled === true,
        // Centro de Alertas v2 (contrato v2 §1): las alertas de Dashbo de la agencia ya son filas del
        // Centro, así que el color de una columna y su aviso son dos números distintos y la tuerca del
        // Tablero edita el del color. Fail-closed: sin el campo, la tuerca sigue en sólo lectura.
        systemAlertsConverted: payload.system_alerts_converted === true,
        // «Inversión sin control» (UD-5, 17/10): la solapa `/alerts/sin-control` y la tarjeta que lleva a
        // ella, abiertas a TODA agencia con la 2.1 (el resto del Centro sigue con su flag). Lo resuelve el
        // backend (`uncontrolled_spend_enabled`). Fail-closed y sin derivarlo de otro flag: el backend sale
        // antes que este bundle, y sin el campo (cache viejo) la tarjeta no pide un endpoint que daría 404.
        uncontrolledSpendEnabled: payload.uncontrolled_spend_enabled === true,
        // Shopify por link de instalación (tanda 23/10): la agencia pide un link hecho para su tienda en vez
        // del OAuth de la app pública, que Shopify todavía no verificó. Lo prende fede agencia por agencia
        // desde el CC; vale en los dos marcos. Fail-closed: sin el campo (backend viejo, cache viejo) todo
        // queda como antes, con el OAuth y «Mandar link al cliente».
        shopifyInstallByLink: payload.shopify_install_by_link === true,
    };
}

// `avatarUrl` es la foto de perfil de Google del usuario, que el backend ya guarda en cada login
// (claim `picture` del ID token, sin scopes extra). Puede venir vacía: los usuarios que entran con
// email + contraseña nunca la tienen, así que el topbar mantiene las iniciales como fallback.
const EMPTY_CHROME = {userName: '', avatarUrl: '', isSuperAdmin: false, planName: '', agency: null};

// Lo último que vimos de ESTE usuario. `agency` no viaja acá: tiene su propio cache.
function cachedChromeFor(userId) {
    if (!userId) return EMPTY_CHROME;
    const cached = readCachedFor(CHROME_CACHE_KEY, userId);
    return cached ? {...EMPTY_CHROME, ...cached} : EMPTY_CHROME;
}

// Exportado para los componentes de clase que lo leen con `static contextType` (`AddStoreForm`,
// `ReconnectAdAccountForm`, `EcommerceConnectionsComponent`). Los de función usan los hooks de abajo.
export const UserModulesContext = createContext({
    ...EMPTY_STATE,
    uiVersion: UI_VERSION_V1,
    uiV2Allowed: false,
    ficha21: false,
    setUiVersion: () => {},
    refresh: () => {},
    chrome: EMPTY_CHROME,
});

export function UserModulesProvider({children}) {
    // `App` no se re-renderiza al navegar (react-router reusa el mismo elemento), así que leer el
    // userId una sola vez dejaba al provider ciego: un login sin recarga nunca disparaba el fetch y
    // el piloto no veía v2 hasta apretar F5. Colgarse de la ubicación nos re-evalúa en cada
    // navegación, que es exactamente cuando la sesión puede haber cambiado (login, logout, switch).
    const {pathname} = useLocation();
    const [session, setSession] = useState(readSession);
    const {userId, isClientUser} = session;
    const canHaveModules = !!userId && !isClientUser;

    useEffect(() => {
        const next = readSession();
        setSession(prev => (
            prev.userId === next.userId && prev.isClientUser === next.isClientUser ? prev : next
        ));
    }, [pathname]);

    const [state, setState] = useState(EMPTY_STATE);
    const [chrome, setChrome] = useState(() => cachedChromeFor(session.userId));
    // La preferencia de UI es DEL USUARIO. Se recalcula en el mismo render en que cambia la sesión
    // (estado derivado, patrón de React): con un efecto habría un render intermedio con la del
    // usuario anterior, y un login SPA con otra cuenta alcanzaba a pintar su marco.
    const prefUserId = canHaveModules ? userId : null;
    const [preferenceState, setPreferenceState] = useState(() => ({
        userId: prefUserId,
        value: readUiVersionFromUrl() || readUiVersionPreference(prefUserId),
    }));
    let preference = preferenceState.value;
    if (preferenceState.userId !== prefUserId) {
        const next = {userId: prefUserId, value: readUiVersionFromUrl() || readUiVersionPreference(prefUserId)};
        setPreferenceState(next);
        preference = next.value;
    }

    // El marco nuevo se ofrece sólo al tier UX_21 — es la condición de agencia. Que el usuario lo
    // esté VIENDO depende además de su preferencia (ver `uiVersion` más abajo).
    const uiV2Allowed = state.experienceTier === TIER_UX_21;

    // Un `?ui=` en la URL vale como si el usuario hubiera tocado el switch — pero sólo se persiste
    // si la agencia está habilitada: si no, un link viejo dejaría al usuario opt-in silencioso para
    // el día que entre al piloto, meses después y sin haberlo pedido.
    useEffect(() => {
        const fromUrl = readUiVersionFromUrl();
        if (fromUrl && uiV2Allowed) writeUiVersionPreference(prefUserId, fromUrl);
    }, [uiV2Allowed, prefUserId]);

    // Un contador que se incrementa para forzar un re-fetch (ver `refresh`). Es la dependencia que
    // dispara de nuevo el efecto sin tocar la sesión.
    const [reloadToken, setReloadToken] = useState(0);
    // Cuándo se pidió por última vez el payload y cuál fue el último que llegó: lo usa el refresco
    // silencioso de abajo.
    const fetchedAtRef = useRef(0);
    const lastPayloadRef = useRef(null);
    // Número del último pedido de módulos, de cualquiera de los dos caminos (la carga y el refresco
    // silencioso): una respuesta que no es la del último pedido se descarta. Sin esto, un refresco
    // silencioso que tardaba podía pisar lo que trajo un «volver a cargar» posterior (review C-…-07).
    const requestSeqRef = useRef(0);
    // El payload con un CAMBIO DE MARCO (UX 2.1 prendida o apagada desde el CC) que llegó en un
    // refresco por foco: espera a la próxima navegación para aplicarse (C-…-08, decisión de fede, 06/10).
    // Al volver a la pestaña el usuario puede tener abierto el modal de objetivos o el cajón de una
    // línea, y cambiarle el marco y la ficha debajo podía hacerle perder lo que estaba editando.
    const pendingFramePayloadRef = useRef(null);
    const stateRef = useRef(state);
    stateRef.current = state;

    useEffect(() => {
        if (!canHaveModules) {
            setState({...EMPTY_STATE, loaded: true});
            setChrome(EMPTY_CHROME);
            return;
        }

        fetchedAtRef.current = 0;
        lastPayloadRef.current = null;
        pendingFramePayloadRef.current = null;
        // Arranque optimista con lo último que vimos de ESTE usuario, para no parpadear.
        const cached = readCachedFor(MODULES_CACHE_KEY, userId);
        setState(cached ? stateFromPayload(cached, false) : EMPTY_STATE);
        setChrome({
            ...cachedChromeFor(userId),
            agency: readCachedFor(AGENCY_CACHE_KEY, userId),
        });

        let cancelled = false;
        const seq = ++requestSeqRef.current;
        // APIClient directo en vez de `AccessService.GetUserModules`: ese helper sólo bifurca
        // 'OK'/'ERROR' y se come el 'NETWORK_ERROR', dejando al provider colgado en loaded:false —
        // con el marco v2 ya pintado y el sidebar sin datos, o sea sin navegación (mismo patrón que
        // ClientActivityLogContent).
        // `cache` comparte el request con los otros callers del arranque que piden el MISMO endpoint
        // (hoy `Default.js` en la home) en vez de duplicarlo. Un `refresh()` explícito lo saltea:
        // el usuario apretó "volver a cargar" justamente para no comerse la respuesta anterior.
        new APIClient(AccessService.GetUserModulesUrl(userId)).call('GET', {}, {cache: reloadToken === 0}).then((data) => {
            if (cancelled || seq !== requestSeqRef.current) return;
            // El reloj del refresco silencioso arranca con la respuesta, sea cual sea: si la PRIMERA
            // carga fallaba, el refresco no arrancaba nunca y la pestaña quedaba toda la sesión con el
            // cache viejo (o sin módulos), que es justo el síntoma de la card (review C-…-06).
            fetchedAtRef.current = Date.now();
            if (data && data.status === 'OK' && data.data) {
                writeJson(MODULES_CACHE_KEY, {userId, value: data.data});
                lastPayloadRef.current = JSON.stringify(data.data);
                pendingFramePayloadRef.current = null;
                setState(stateFromPayload(data.data));
                return;
            }
            // Cualquier otro desenlace (ERROR, NETWORK_ERROR, respuesta rara): nos quedamos con el
            // cache si lo hay y damos la carga por terminada. El enforcement real de permisos vive
            // en cada endpoint; esto sólo decide qué dibujar.
            lastPayloadRef.current = null;
            setState(prev => ({...prev, loaded: true}));
        });

        return () => {
            cancelled = true;
        };
    }, [userId, canHaveModules, reloadToken]);

    // Reintento para las pantallas que ofrecen "volver a cargar" cuando el fetch de módulos falló
    // sin cache. Sin esto la única salida era `window.location.reload()`, que recarga la app entera
    // y se lleva puesto el estado de la pantalla.
    const refresh = useCallback(() => {
        setState(prev => ({...prev, loaded: false}));
        setReloadToken(token => token + 1);
    }, []);

    // Refresco SILENCIOSO al navegar y al volver a la pestaña, si el payload tiene más de
    // `MODULES_STALE_MS` (card IPm89r9a): la Memoria, el Media Plan, Informes o la UX 2.1 se prenden
    // desde el Command Center sin deploy, y el payload se pedía una sola vez por sesión — la agencia
    // no veía lo que le acababan de prender hasta volver a loguearse. No pasa por `loaded: false` (no
    // hay loader ni parpadeo) y sólo cambia el estado si el payload cambió: los `modules` conservan su
    // identidad y no re-disparan los efectos de las pantallas.
    //
    // `fromNavigation`: el refresco por foco aplica todo MENOS el cambio de marco (`experience_tier`),
    // que queda guardado y se aplica en la próxima navegación (ver `pendingFramePayloadRef`).
    const refreshIfStale = useCallback(({fromNavigation}) => {
        if (!canHaveModules || !fetchedAtRef.current) return;
        if (Date.now() - fetchedAtRef.current < MODULES_STALE_MS) return;
        fetchedAtRef.current = Date.now();
        const requestedFor = userId;
        const seq = ++requestSeqRef.current;
        new APIClient(AccessService.GetUserModulesUrl(userId)).call('GET', {}, {cache: false}).then((data) => {
            if (seq !== requestSeqRef.current) return;
            if (!data || data.status !== 'OK' || !data.data) return;
            if (String(LocalStorage.fetchUserId() || '') !== String(requestedFor)) return;
            const json = JSON.stringify(data.data);
            if (json === lastPayloadRef.current) return;
            lastPayloadRef.current = json;
            writeJson(MODULES_CACHE_KEY, {userId: requestedFor, value: data.data});
            const currentTier = stateRef.current.experienceTier;
            if (!fromNavigation && tierFromPayload(data.data) !== currentTier) {
                pendingFramePayloadRef.current = data.data;
                setState(stateFromPayload({...data.data, experience_tier: currentTier}));
                return;
            }
            pendingFramePayloadRef.current = null;
            // `stateFromPayload` deja `loaded: true`: si esto llega después de una carga que falló sin
            // cache, la pantalla sale del estado vacío con los módulos que sí llegaron.
            setState(stateFromPayload(data.data));
        });
    }, [canHaveModules, userId]);

    useEffect(() => {
        // Primero el cambio de marco que esperaba una navegación; después, el refresco de siempre.
        const pending = pendingFramePayloadRef.current;
        if (pending) {
            pendingFramePayloadRef.current = null;
            setState(stateFromPayload(pending));
        }
        refreshIfStale({fromNavigation: true});
    }, [pathname, refreshIfStale]);

    useEffect(() => {
        const onFocus = () => refreshIfStale({fromNavigation: false});
        const onVisible = () => {
            if (document.visibilityState === 'visible') onFocus();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [refreshIfStale]);

    const uiVersion = useMemo(() => {
        if (!uiV2Allowed) return UI_VERSION_V1;
        return preference || UI_VERSION_V2;
    }, [uiV2Allowed, preference]);

    // Datos del marco: sólo en v2 (en v1 los pide `Layout.js`, que es quien los usa).
    useEffect(() => {
        if (uiVersion !== UI_VERSION_V2 || !canHaveModules) return;

        let cancelled = false;

        // Este endpoint lo piden además el hook de estado de agencia y el chat de soporte, todos al
        // montar: con `cache` los tres comparten un request en lugar de hacer tres.
        new APIClient(API_URL + 'get-logged-user-data/').call('GET', {}, {cache: true}).then((data) => {
            if (cancelled || !data || data.status !== 'OK') return;
            const isSuperAdmin = data.is_super_admin === true;
            // Persistirlo NO es cosmético: `FeatureFlags.isSuperAdmin()` lee de LocalStorage, y sin
            // esto un superadmin que navega siempre en v2 se queda sin las features gateadas por ese
            // flag (tab del portal, media plan, bitácora) — justo los usuarios del piloto.
            LocalStorage.updateIsSuperAdmin(isSuperAdmin);
            if (data.agency_id) LocalStorage.updateAgencyId(String(data.agency_id));
            const identity = {
                userName: data.name || data.email || '',
                avatarUrl: data.profile_picture_url || '',
                isSuperAdmin,
                // El plan contratado, para mostrarlo junto al nombre de la agencia. Viaja acá y no
                // por `get-agency-data/` porque ese endpoint no lo devuelve: el prototipo lo pedía y
                // durante meses fue una línea vacía en el menú.
                planName: data.plan_name || '',
            };
            writeJson(CHROME_CACHE_KEY, {userId, value: identity});
            setChrome(prev => ({...prev, ...identity}));
        });

        new APIClient(API_URL + 'get-agency-data/').call('GET').then((response) => {
            if (cancelled || !response || response.status !== 'OK') return;
            writeJson(AGENCY_CACHE_KEY, {userId, value: response.general_data});
            setChrome(prev => ({...prev, agency: response.general_data}));
        });

        return () => {
            cancelled = true;
        };
    }, [uiVersion, canHaveModules, userId]);

    const setUiVersion = useCallback((version) => {
        const next = version === UI_VERSION_V2 ? UI_VERSION_V2 : UI_VERSION_V1;
        writeUiVersionPreference(prefUserId, next);
        setPreferenceState({userId: prefUserId, value: next});
    }, [prefUserId]);

    const value = useMemo(() => ({
        ...state,
        uiVersion,
        uiV2Allowed,
        // La ficha 2.1 NO es sólo el tier: un usuario de UX_21 que volvió al clásico ve la ficha
        // vieja. Por eso se calcula acá y no en `stateFromPayload`, que corre antes de resolver
        // `uiVersion`. Volver al clásico = ver exactamente lo que ve una agencia de tier IA.
        ficha21: uiV2Allowed && uiVersion === UI_VERSION_V2,
        setUiVersion,
        refresh,
        chrome,
    }), [state, uiVersion, uiV2Allowed, setUiVersion, refresh, chrome]);

    return (
        <UserModulesContext.Provider value={value}>
            {children}
        </UserModulesContext.Provider>
    );
}

export const useUserModules = () => useContext(UserModulesContext);

/**
 * Hook del marco. `canUseV2` = la agencia está en UX_21 (gobierna si el switch se muestra);
 * `isV2` = además el usuario no eligió volver al clásico.
 */
export function useUiVersion() {
    const {uiVersion, uiV2Allowed, setUiVersion, loaded} = useUserModules();
    return {
        uiVersion,
        isV2: uiVersion === UI_VERSION_V2,
        canUseV2: uiV2Allowed,
        setUiVersion,
        loaded,
    };
}

/**
 * Hook de la ficha del cliente rediseñada. `ficha21` ya incorpora la preferencia de UI: en clásico
 * es false aunque la agencia sea UX_21. `mediaPlanEnabled` y `clientMemoryLevel` NO — vienen del
 * tier y sobreviven al switch, porque no son parte del rediseño.
 *
 * `loaded` importa: hasta que llega el payload, `ficha21` es false. Una pantalla que redirige según
 * el flag debe esperar a `loaded` para no rebotar al usuario por un valor que todavía no llegó.
 */
export function useFicha21() {
    const {ficha21, mediaPlanEnabled, clientMemoryLevel, modules, loaded, refresh,
        clientEconomicsEnabled} = useUserModules();
    return {ficha21, mediaPlanEnabled, clientMemoryLevel, modules, loaded, refresh,
        clientEconomicsEnabled: clientEconomicsEnabled === true};
}

/**
 * El widget de feedback es parte de la UX 2.1: se dibuja si la agencia lo tiene (flag del backend,
 * hoy = la agencia tiene la 2.1) Y el usuario está VIENDO la 2.1. Un usuario que volvió al Dashbo
 * clásico no lo ve, aunque su agencia sea 2.1.
 *
 * Se compone acá y no en el backend porque la preferencia clásico↔2.1 vive en este browser.
 */
export function feedbackWidgetVisible({feedbackWidgetEnabled, uiVersion}) {
    return feedbackWidgetEnabled === true && uiVersion === UI_VERSION_V2;
}

/**
 * Hook del widget de feedback in-app.
 *
 * `loaded` importa tanto como `enabled`: hasta que llega el payload no sabemos si a la agencia le
 * corresponde, y montar la pestaña antes produce un parpadeo coral en el borde de la pantalla de
 * cualquier agencia. El widget espera.
 */
export function useFeedbackWidget() {
    const {feedbackWidgetEnabled, loaded, uiVersion, experienceTier} = useUserModules();
    return {
        enabled: feedbackWidgetVisible({feedbackWidgetEnabled, uiVersion}),
        loaded,
        uiVersion,
        experienceTier,
    };
}

/**
 * Hook del Centro de Alertas: la pantalla «Alertas», su entrada en la barra y los links que llevan
 * a ella. Es de la 2.1: con el flag de la agencia pero viendo el clásico, no existe (`enabled`
 * false). `loaded` importa: una ruta que redirige sin el flag tiene que esperar el payload.
 */
export function useAlertRulesAccess() {
    const {alertRulesEnabled, systemAlertsConverted, loaded, uiVersion} = useUserModules();
    // `converted` (v2): la agencia ya tiene las alertas de Dashbo como filas del Centro.
    return {enabled: alertRulesVisible({alertRulesEnabled, uiVersion}), loaded, converted: systemAlertsConverted === true};
}

/** Las dos mitades del Centro de Alertas: el flag de la agencia Y viendo la 2.1. Fail-closed. */
export function alertRulesVisible({alertRulesEnabled, uiVersion}) {
    return alertRulesEnabled === true && uiVersion === UI_VERSION_V2;
}

/**
 * Hook de «Inversión sin control» (UD-5): la solapa `/alerts/sin-control` y la tarjeta gris del Tablero, la
 * ficha, el Listado y «Ahora». Es de la 2.1, como el Centro, pero con permiso propio: la solapa se abre a toda
 * agencia con la 2.1 aunque no tenga el Centro. `loaded` importa: la ruta redirige sin el permiso y tiene que
 * esperar el payload.
 */
export function useUncontrolledSpendAccess() {
    const {uncontrolledSpendEnabled, loaded, uiVersion} = useUserModules();
    return {enabled: uncontrolledSpendVisible({uncontrolledSpendEnabled, uiVersion}), loaded};
}

/** Las dos mitades de «Inversión sin control»: el permiso del backend Y viendo la 2.1. Fail-closed. */
export function uncontrolledSpendVisible({uncontrolledSpendEnabled, uiVersion}) {
    return uncontrolledSpendEnabled === true && uiVersion === UI_VERSION_V2;
}

/**
 * Hook de Shopify por link de instalación: pedir el link en lugar del OAuth, el aviso de Conexiones con
 * los pedidos y el «Volver a autorizar» de una tienda. Es de la AGENCIA, no del marco: vale igual en la
 * 2.1 y en el clásico. Fail-closed (`=== true`).
 */
export function useShopifyInstallByLink() {
    const {shopifyInstallByLink, loaded} = useUserModules();
    return {enabled: shopifyInstallByLink === true, loaded};
}

/**
 * Hook de las Guías in-app. Igual que el widget de feedback, `loaded` importa tanto como `enabled`:
 * con el cache viejo el flag llega en false y una guía no puede aparecer y desaparecer.
 */
export function useGuidesAccess() {
    const {guidesEnabled, loaded, uiVersion, chrome} = useUserModules();
    return {enabled: guidesEnabled === true, loaded, uiVersion, isSuperAdmin: chrome.isSuperAdmin === true};
}
